"use client";

import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot,
  faArrowAltCircleRight,
  faClock,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { faGithub, faGitlab } from "@fortawesome/free-brands-svg-icons";
import Recommandations from "../../components/Recommandations.jsx";
import Script from "next/script";
import { useAllArticles } from "../../lib/hooks/allArticlesCosmic.js";
import tabWebRealisations from "../../lib/data/dataWeb.js";
import { motion } from "motion/react";
import WebRealisationCard from "../../components/WebRealisationCard.jsx";
import "react-medium-image-zoom/dist/styles.css";
import AnimatedSection from "../../components/AnimatedSection.jsx";
import RealisationsWebPresentation from "../../components/RealisationsWebPresentation.jsx";
import QuiSuisJeSection from "../../components/QuiSuisJeSection.jsx";
import PropTypes from "prop-types";
import Menu from "../../components/MenuRecrutement.jsx";
import Footer from "../../components/Footer.jsx";

import BlogSection from "../../components/BlogSection.jsx";

// En-tête animé sans duplication
function HeaderContent() {
  return (
    <h1 className="mb-6 md:text-4xl text-2xl flex items-start justify-start">
      <span>Besoin d&apos;un développeur Web ?</span>
    </h1>
  );
}

function MotionHeader({ children }) {
  return (
    <motion.div
      initial={{ x: "-100vw" }}
      animate={{ x: "0" }}
      transition={{ type: "spring", stiffness: 20 }}>
      {children}
    </motion.div>
  );
}

MotionHeader.propTypes = {
  children: PropTypes.node,
};
export default function HomeClient({ jsonLdReviews }) {
  const [isClient, setIsClient] = useState(false);
  const { articles } = useAllArticles(4);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Filtrer les projets marqués pour l'accueil
  const featuredProjects = tabWebRealisations.filter(
    (projet) => projet.accueil === true,
  );
  const sites = featuredProjects.map((data, k) => (
    <WebRealisationCard key={"sites_" + k} data={data} />
  ));

  return (
    <>
      {/* Script JSON-LD avis injecté côté serveur (vérifiez dans le HTML source) */}
      {jsonLdReviews && (
        <Script
          id="jsonld-reviews"
          type="application/ld+json"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonLdReviews),
          }}
        />
      )}

      <header>
        <Menu />
      </header>
      <main id="main">
        <div className="relative h-[300px] overflow-hidden bg-[url('/ordi-cafe-fond.webp')] bg-cover bg-[20%] bg-no-repeat">
          <div className="absolute bottom-0 left-0 right-0 top-0 h-full w-full overflow-hidden bg-fixed">
            <div className="flex h-full items-center justify-end">
              <div className="lg:w-1/2 mt-28 mx-10">
                {isClient ? (
                  <MotionHeader>
                    <HeaderContent />
                  </MotionHeader>
                ) : (
                  <HeaderContent />
                )}
                <p className="mb-5 colortxt flex items-center">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className="w-6 h-6 mr-2"
                  />{" "}
                  Marseille / Aix-en-Provence
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="m-auto">
          <section id="services" className="services">
            <div className="text-gray-900">
              {/* Header */}
              <header className="text-center py-6 mx-10">
                <h2 className="text-3xl mb-5 text-bleuciel leading-2">
                  <FontAwesomeIcon icon={faSearch} className="mr-3" /> Ouverte à
                  toutes opportunités :
                </h2>
                <p className="text-lg text-rose">
                  Développeur Web Javascript | Front + Back End sénior
                  <br />
                  React / NextJs | VueJS / NuxtJs | NodeJS
                </p>
                <p>
                  <span className="rounded-full bg-gray-200 text-gris text-sm px-2 py-1">
                    CDI
                  </span>{" "}
                  <span className="rounded-full bg-gray-200 text-gris text-sm px-2 py-1">
                    CDD
                  </span>{" "}
                  <span className="rounded-full bg-gray-200 text-gris text-sm px-2 py-1">
                    Freelance
                  </span>{" "}
                  <span className="rounded-full bg-gray-200 text-gris text-sm px-2 py-1">
                    Temps plein
                  </span>{" "}
                  <span className="rounded-full bg-gray-200 text-gris text-sm px-2 py-1">
                    Emploi à long terme
                  </span>
                </p>

                <p className="mt-2">
                  <FontAwesomeIcon icon={faClock} className="mr-3" />
                  Disponible sous préavis d&apos;1 mois pour un CDI ou CDD, ou
                  immédiatement pour une mission freelance.
                </p>
              </header>
              {/* Qui suis-je */}
              <div className="bg-gray-100">
                <QuiSuisJeSection isClient={isClient} />
              </div>
              {/* Réalisations */}
              <section className="py-6 ">
                {isClient ? (
                  <AnimatedSection
                    id="creawebsite"
                    className="blog skills section-bg text-center ">
                    <div className="mx-10">
                      <div className="text-bleuciel text-3xl text-center mb-5">
                        <h2>Mes projets</h2>
                      </div>
                      <RealisationsWebPresentation />

                      <div className="flex justify-center my-6 items-center">
                        Extraits de code sur :
                        <motion.a
                          whileHover="hover"
                          href="https://github.com/SandrineFial/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-rose text-white rounded-full hover:bg-pink-600 transition duration-300 mx-2"
                          aria-label="Voir mon code sur GitHub"
                          title="Voir mon code sur GitHub">
                          <motion.div
                            className="mr-2"
                            variants={{
                              rest: { rotate: 0 },
                              hover: {
                                rotate: 360,
                                transition: { duration: 0.6 },
                              },
                            }}>
                            <FontAwesomeIcon icon={faGithub} />
                          </motion.div>
                          GitHub
                        </motion.a>
                        <motion.a
                          whileHover="hover"
                          href="https://github.com/sf-web/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-rose text-white rounded-full hover:bg-pink-600 transition duration-300"
                          aria-label="Voir mon code sur GitLab"
                          title="Voir mon code sur GitLab">
                          <motion.div
                            className="mr-2"
                            variants={{
                              rest: { rotate: 0 },
                              hover: {
                                rotate: 360,
                                transition: { duration: 0.6 },
                              },
                            }}>
                            <FontAwesomeIcon icon={faGitlab} />
                          </motion.div>
                          GitLab
                        </motion.a>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {sites}
                      </div>

                      {/* Bouton pour voir tous les projets */}
                      <div className="mt-8 text-center">
                        <motion.a
                          href="/projets"
                          className="inline-flex items-center px-6 py-3 bg-bleuciel text-white rounded-full hover:bg-blue-700 transition-colors duration-300 font-medium"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}>
                          <FontAwesomeIcon
                            icon={faArrowAltCircleRight}
                            className="mr-2"
                          />
                          Voir tous mes projets ({tabWebRealisations.length})
                        </motion.a>
                      </div>
                    </div>
                  </AnimatedSection>
                ) : (
                  <div
                    id="creawebsite"
                    className="blog skills section-bg text-center ">
                    <div className="container m-auto">
                      <div className="text-bleuciel text-3xl text-center mb-5">
                        <h2>Mes projets</h2>
                      </div>
                      <RealisationsWebPresentation />

                      <div className="flex justify-center my-6">
                        <a
                          href="https://github.com/SandrineFial/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-rose text-white rounded-full hover:bg-pink-600 transition duration-300"
                          aria-label="Voir mon code sur GitHub"
                          title="Voir mon code sur GitHub">
                          <FontAwesomeIcon icon={faGithub} className="mr-2" />
                          Mon code sur GitHub
                        </a>
                      </div>
                      <div className="grid md:grid-cols-2 grid-cols-1 justify-between">
                        {sites}
                      </div>

                      {/* Bouton pour voir tous les projets */}
                      <div className="mt-8 text-center">
                        <a
                          href="/projets"
                          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300 font-medium">
                          <FontAwesomeIcon
                            icon={faArrowAltCircleRight}
                            className="mr-2"
                          />
                          Voir tous mes projets ({tabWebRealisations.length})
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </section>

          <section className="container m-auto bg-gray-50">
            <div className="mx-10">
              <h2 className="text-3xl text-bleuciel text-center mb-5">
                Ma Méthodologie de Travail
              </h2>

              <p className="mb-2 text-center">
                Voici la démarche que je met en place pour mener à bien un
                projet Web de A à Z :
              </p>
              <div className="flex justify-center">
                <ol className="list-decimal list-inside m-auto text-left">
                  <li>
                    <strong>Analyse & Cadrage</strong> : Comprendre les besoins
                    client et définir les spécifications.
                  </li>
                  <li>
                    <strong>Conception & Maquettage</strong> : Création des
                    prototypes des pages clés avec Figma ou Miro.
                  </li>
                  <li>
                    <strong>Choix Techniques</strong> : Sélection du{" "}
                    <strong>framework</strong> et du <strong>langage</strong>{" "}
                    adaptés au projet.
                  </li>
                  <li>
                    <strong>Développement Front et BackEnd</strong> :
                    Intégration de l&apos;interface en privilégiant l&apos;
                    <strong>UX</strong>, le <strong>responsive design</strong>{" "}
                    et le Clean Code.
                  </li>
                  <li>
                    <strong>Revues Client</strong> : Présentation des avancées
                    et ajustement selon les retours.
                  </li>
                  <li>
                    <strong>Tests & validation pré-production</strong> : Tests
                    fonctionnels et techniques, correction des anomalies et
                    validation de la qualité du code.
                  </li>
                  <li>
                    <strong>Mise en Production & Tests</strong> : Déploiement de
                    l&apos;application via une pipeline CI/CD (GitHub Actions ou
                    GitLab CI), puis validation de la stabilité, des
                    performances et de la sécurité.
                  </li>
                </ol>
              </div>
            </div>
          </section>

          <BlogSection isClient={isClient} articles={articles} />

          {/* Section des recommandations clients */}
          <Recommandations />

          <section id="contact" className="container m-auto">
            <footer className="mt-6">
              <div className="flex flex-col items-center justify-center">
                <div>
                  <h2 className="text-center text-3xl text-bleuciel mb-5">
                    Me Contacter
                  </h2>
                </div>
                <div className="mb-4 flex items-center justify-center md:justify-start font-bold">
                  Sandrine FIALON
                </div>
                <div className="mb-1">
                  <a
                    href="mailto:fialonsweb&#64;gmail&#46;com"
                    aria-label="Envoyer un email"
                    className=" flex items-center justify-center md:justify-start">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="mr-3 h-5 w-5">
                      <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                      <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
                    </svg>
                    <span className="hover:underline">
                      fialonsweb&#64;gmail&#46;com
                    </span>
                  </a>
                </div>
                <div className="mb-1">
                  <a
                    href="tel:0683874742"
                    rel="noreferrer"
                    aria-label="Appeler"
                    className=" flex items-center justify-center md:justify-start">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="mr-3 h-5 w-5">
                      <path
                        fillRule="evenodd"
                        d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="hover:underline">06 83 87 47 42</span>
                  </a>
                </div>
                <div className="mb-1">
                  <a
                    className=" flex items-center justify-center md:justify-start"
                    href="https://www.linkedin.com/in/fialonsandrine/"
                    rel="noreferrer noopener"
                    aria-label="LinkedIn"
                    title="Réseau LinkedIn"
                    target="_blank">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-3 h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 24 24">
                      <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
                    </svg>
                    <span className="hover:underline">
                      linkedin.com/in/fialonsandrine/
                    </span>
                  </a>
                </div>
                <div className="mb-1">
                  <a
                    className=" flex items-center justify-center md:justify-start"
                    href="https://github.com/SandrineFial"
                    rel="noreferrer noopener"
                    aria-label="GitHub"
                    title="Réseau GitHub"
                    target="_blank">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-3 h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                    </svg>
                    <span className="hover:underline">
                      github.com/SandrineFial
                    </span>
                  </a>
                </div>
                <div className="mb-1">
                  <a
                    className=" flex items-center justify-center md:justify-start"
                    href="https://gitlab.com/sf-web"
                    rel="noreferrer noopener"
                    aria-label="GitLab"
                    title="Réseau GitLab"
                    target="_blank">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-3 h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 24 24">
                      <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 4.82 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0 1 18.6 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.51 1.22 3.78a.84.84 0 0 1-.3.92z" />
                    </svg>
                    <span className="hover:underline">gitlab.com/sf-web</span>
                  </a>
                </div>

                <div className="mb-1 flex items-center justify-center md:justify-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="mr-3 h-5 w-5">
                    <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
                    <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
                  </svg>
                  Marseille 8e
                </div>
              </div>
            </footer>
          </section>
        </div>
        <a
          href="#"
          title="Retour en haut de la page"
          aria-label="Retour en haut de la page"
          className="back-to-top d-flex align-items-center justify-content-center">
          <i className="bi bi-arrow-up-short">&nbsp;</i>
        </a>
      </main>

      <footer>
        <Footer />
      </footer>
    </>
  );
}

HomeClient.propTypes = {
  jsonLdReviews: PropTypes.any,
};
